import { useOnlineStatus, useOfflineQueue } from '../hooks/useOffline';
import { WifiOff, Wifi, RefreshCw, Loader2, CloudOff, History } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import SyncHistoryPanel from './SyncHistoryPanel';

export default function OfflineBanner() {
    const { isOnline, wasOffline } = useOnlineStatus();
    const { pendingCount, syncing, syncQueue } = useOfflineQueue();

    const handleSync = async () => {
        const result = await syncQueue();
        if (result) {
            if (result.synced > 0) toast.success(`Synced ${result.synced} action${result.synced > 1 ? 's' : ''}`);
            if (result.failed > 0) toast.error(`${result.failed} action${result.failed > 1 ? 's' : ''} failed to sync`);
        }
    };

    // Offline banner
    if (!isOnline) {
        return (
            <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-600 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm shadow-lg" data-testid="offline-banner">
                <WifiOff className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">You're offline</span>
                <span className="text-amber-100">Tasks, timeclock, and compounding logs will queue and sync when you reconnect.</span>
                {pendingCount > 0 && (
                    <Badge className="bg-amber-800 text-amber-100 border-amber-500" data-testid="offline-pending-count">
                        <CloudOff className="w-3 h-3 mr-1" />
                        {pendingCount} pending
                    </Badge>
                )}
                <SyncHistoryPanel trigger={
                    <Button size="sm" variant="secondary" className="h-7 text-xs bg-amber-800/60 hover:bg-amber-800 text-amber-100 border-0" data-testid="offline-history-btn">
                        <History className="w-3 h-3 mr-1" /> History
                    </Button>
                } />
            </div>
        );
    }

    // Just came back online with pending items
    if (wasOffline && pendingCount > 0) {
        return (
            <div className="fixed top-0 left-0 right-0 z-[100] bg-emerald-600 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm shadow-lg" data-testid="sync-banner">
                <Wifi className="w-4 h-4" />
                <span className="font-medium">Back online!</span>
                <span className="text-emerald-100">{pendingCount} action{pendingCount > 1 ? 's' : ''} ready to sync.</span>
                <Button size="sm" variant="secondary" onClick={handleSync} disabled={syncing} className="h-7 text-xs" data-testid="sync-now-btn">
                    {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Sync Now
                </Button>
            </div>
        );
    }

    // Persistent pending indicator (small, non-intrusive)
    if (pendingCount > 0) {
        return (
            <div className="fixed top-2 right-4 z-[90] flex items-center gap-2" data-testid="pending-indicator">
                <SyncHistoryPanel trigger={
                    <Button size="sm" variant="outline" className="h-8 text-xs border-muted-foreground/30 text-muted-foreground hover:text-foreground" data-testid="pending-history-btn">
                        <History className="w-3 h-3 mr-1" /> Sync Log
                    </Button>
                } />
                <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}
                    className="h-8 text-xs border-amber-500/40 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30">
                    {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CloudOff className="w-3 h-3 mr-1" />}
                    {pendingCount} pending sync
                </Button>
            </div>
        );
    }

    return null;
}
